import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
	MessageSquare,
	Search,
	Eye,
	EyeOff,
	AlertTriangle,
	CheckCircle,
	Phone,
	Mail,
	Shield,
	ArrowLeft,
	Flag,
	Clock,
	Baby,
} from "lucide-react";
import styles from './Admin.module.css';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNavigate } from "react-router-dom";
import AdminMessageThread from "@/components/admin/AdminMessageThread";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

interface AdminConversation {
	id: string;
	parent_user: {
		id: string;
		full_name: string;
		avatar_url: string | null;
		email: string;
	};
	doula_user: {
		id: string;
		full_name: string;
		avatar_url: string | null;
		email: string;
	};
	appointment?: {
		id: string;
		appointment_date: string;
		status: string;
		duration_minutes?: number;
	};
	appointments?: Array<{
		id: string;
		appointment_date: string;
		status: string;
		duration_minutes?: number;
		google_meet_link?: string | null;
		notes?: string | null;
	}>;
	last_message?: {
		id: string;
		content: string;
		created_at: string;
		sender_type: "parent" | "doula";
	};
	unread_count: number;
	created_at: string;
	flagged_messages: number;
	has_contact_info: boolean;
	is_flagged: boolean;
	admin_dismissed: boolean;
}

interface FlaggedMessage {
	id: string;
	content: string;
	created_at: string;
	sender_type: "parent" | "doula";
	conversation_id: string;
	flag_reason: "phone" | "email" | "external_link" | "suspicious";
	is_dismissed: boolean;
}

interface ConversationResponse {
	id: string;
	parent_user: {
		id: string;
		full_name: string;
		avatar_url: string | null;
		email: string;
	};
	doula_user: {
		id: string;
		full_name: string;
		avatar_url: string | null;
		email: string;
	};
	appointment?: {
		id: string;
		appointment_date: string;
		status: string;
		duration_minutes?: number;
	};
	last_message?: {
		id: string;
		content: string;
		created_at: string;
		sender_type: "parent" | "doula";
	};
	unread_count: number;
	created_at: string;
	flagged_messages_count: number;
	has_contact_info: boolean;
	is_flagged: boolean;
}

export default function AdminMessagingMonitor() {
	const navigate = useNavigate();
	const { user } = useAuth();
	const [conversations, setConversations] = useState<AdminConversation[]>([]);
	const [flaggedMessages, setFlaggedMessages] = useState<FlaggedMessage[]>([]);
	const [filteredConversations, setFilteredConversations] = useState<
		AdminConversation[]
	>([]);
	const [loading, setLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState("");
	const [filterType, setFilterType] = useState("all");
	const [selectedConversation, setSelectedConversation] = useState<
		string | null
	>(null);
	const [activeTab, setActiveTab] = useState("conversations");
	const [conversationMessages, setConversationMessages] = useState<any[]>([]);
	const [loadingMessages, setLoadingMessages] = useState(false);

	useEffect(() => {
		loadAllConversations();
	}, [filterType]);

	useEffect(() => {
		filterConversations();
	}, [conversations, searchTerm, filterType]);

	useEffect(() => {
		if (conversations.length > 0) {
			loadFlaggedMessages();
		}
	}, [conversations]);

	const loadAllConversations = async () => {
		try {
			setLoading(true);

			if (!user) {
				throw new Error("User not authenticated");
			}

			const { data: session } = await supabase.auth.getSession();
			if (!session?.session?.access_token) {
				throw new Error("No session found");
			}

			const response = await fetch(
				`${supabase.supabaseUrl}/functions/v1/get-all-conversations`,
				{
					method: "POST",
					headers: {
						Authorization: `Bearer ${session.session.access_token}`,
						"Content-Type": "application/json",
					},
					body: JSON.stringify({
						filter: {
							status: filterType === "all" ? null : filterType,
							search: searchTerm || null,
						},
						pagination: {
							page: 1,
							limit: 100,
						},
					}),
				},
			);

			if (!response.ok) {
				const errorText = await response.text();
				throw new Error(`HTTP ${response.status}: ${errorText}`);
			}

			const result = await response.json();

			if (!result.success) {
				throw new Error(result.error || "Failed to fetch conversations");
			}

			// Transform the data to match our interface
			const transformedConversations: AdminConversation[] =
				result.data.conversations.map((conv: ConversationResponse) => ({
					id: conv.id,
					parent_user: conv.parent_user,
					doula_user: conv.doula_user,
					appointment: conv.appointment,
					last_message: conv.last_message,
					unread_count: conv.unread_count,
					created_at: conv.created_at,
					flagged_messages: conv.flagged_messages_count,
					has_contact_info: conv.has_contact_info,
					is_flagged: conv.is_flagged,
					admin_dismissed: false, // This field might need to be added to the database
				}));

			setConversations(transformedConversations);
		} catch (error) {
			setConversations([]);
		} finally {
			setLoading(false);
		}
	};

	const loadFlaggedMessages = async () => {
		try {
			// Flagged messages are now included in conversation data
			// We can extract them from conversations that have is_flagged: true
			const flaggedConversations = conversations.filter(
				(conv) => conv.is_flagged,
			);

			// For now, we'll use the last message from flagged conversations as flagged messages
			const flaggedMsgs: FlaggedMessage[] = flaggedConversations
				.filter((conv) => conv.last_message)
				.map((conv) => ({
					id: conv.last_message!.id,
					content: conv.last_message!.content,
					created_at: conv.last_message!.created_at,
					sender_type: conv.last_message!.sender_type as "parent" | "doula",
					conversation_id: conv.id,
					flag_reason: "phone", // This would need to be determined by the flagging logic
					is_dismissed: false,
				}));

			setFlaggedMessages(flaggedMsgs);
		} catch (error) {
			setFlaggedMessages([]);
		}
	};

	const loadConversationMessages = async (conversationId: string) => {
		try {
			setLoadingMessages(true);

			if (!user) {
				throw new Error("User not authenticated");
			}

			const { data: session } = await supabase.auth.getSession();
			if (!session?.session?.access_token) {
				throw new Error("No session found");
			}

			const response = await fetch(
				`${supabase.supabaseUrl}/functions/v1/get-conversation-messages`,
				{
					method: "POST",
					headers: {
						Authorization: `Bearer ${session.session.access_token}`,
						"Content-Type": "application/json",
					},
					body: JSON.stringify({
						conversation_id: conversationId,
						pagination: {
							page: 1,
							limit: 100,
						},
					}),
				},
			);

			if (!response.ok) {
				const errorText = await response.text();
				throw new Error(`HTTP ${response.status}: ${errorText}`);
			}

			const result = await response.json();

			if (!result.success) {
				throw new Error(result.error || "Failed to fetch messages");
			}

			setConversationMessages(result.data.messages);
		} catch (error) {
			setConversationMessages([]);
		} finally {
			setLoadingMessages(false);
		}
	};

	const dismissConversationFlags = async (conversationId: string) => {
		try {
			if (!user) {
				throw new Error("User not authenticated");
			}

			const { data: session } = await supabase.auth.getSession();
			if (!session?.session?.access_token) {
				throw new Error("No session found");
			}

			const response = await fetch(
				`${supabase.supabaseUrl}/functions/v1/dismiss-conversation-flags`,
				{
					method: "POST",
					headers: {
						Authorization: `Bearer ${session.session.access_token}`,
						"Content-Type": "application/json",
					},
					body: JSON.stringify({
						conversation_id: conversationId,
					}),
				},
			);

			if (!response.ok) {
				const errorText = await response.text();
				throw new Error(`HTTP ${response.status}: ${errorText}`);
			}

			const result = await response.json();

			if (!result.success) {
				throw new Error(result.error || "Failed to dismiss flags");
			}

			// Update local state - mark conversation as no longer flagged
			setConversations((prev) =>
				prev.map((conv) =>
					conv.id === conversationId
						? {
							...conv,
							is_flagged: false,
							flagged_messages: 0,
							admin_dismissed: true,
						}
						: conv,
				),
			);

			// Reload flagged messages
			loadFlaggedMessages();
		} catch (error) {
			// Handle error silently
		}
	};

	const filterConversations = () => {
		let filtered = conversations;

		// Filter by search term
		if (searchTerm) {
			filtered = filtered.filter(
				(conv) =>
					conv.parent_user.full_name
						.toLowerCase()
						.includes(searchTerm.toLowerCase()) ||
					conv.doula_user.full_name
						.toLowerCase()
						.includes(searchTerm.toLowerCase()) ||
					conv.last_message?.content
						.toLowerCase()
						.includes(searchTerm.toLowerCase()),
			);
		}

		// Filter by type
		switch (filterType) {
			case "flagged":
				filtered = filtered.filter(
					(conv) => conv.is_flagged && !conv.admin_dismissed,
				);
				break;
			case "contact-info":
				filtered = filtered.filter((conv) => conv.has_contact_info);
				break;
			case "recent":
				filtered = filtered.filter((conv) => {
					const daysSince = Math.floor(
						(Date.now() -
							new Date(
								conv.last_message?.created_at || conv.created_at,
							).getTime()) /
						(1000 * 60 * 60 * 24),
					);
					return daysSince <= 7;
				});
				break;
			case "unread":
				filtered = filtered.filter((conv) => conv.unread_count > 0);
				break;
			default:
				break;
		}

		// Sort flagged conversations first
		filtered.sort((a, b) => {
			if (
				a.is_flagged &&
				!a.admin_dismissed &&
				(!b.is_flagged || b.admin_dismissed)
			)
				return -1;
			if (
				b.is_flagged &&
				!b.admin_dismissed &&
				(!a.is_flagged || a.admin_dismissed)
			)
				return 1;

			// Then sort by most recent message
			const aTime = new Date(
				a.last_message?.created_at || a.created_at,
			).getTime();
			const bTime = new Date(
				b.last_message?.created_at || b.created_at,
			).getTime();
			return bTime - aTime;
		});

		setFilteredConversations(filtered);
	};

	const dismissFlaggedConversation = async (conversationId: string) => {
		await dismissConversationFlags(conversationId);
	};

	const dismissFlaggedMessage = async (messageId: string) => {
		try {
			// Find the conversation for this message and dismiss all flags
			const flaggedMessage = flaggedMessages.find(
				(msg) => msg.id === messageId,
			);
			if (flaggedMessage) {
				await dismissConversationFlags(flaggedMessage.conversation_id);
			}
		} catch (error) {
			// Handle error silently
		}
	};

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
			year: "numeric",
		});
	};

	const formatTime = (dateString: string) => {
		const date = new Date(dateString);
		const now = new Date();
		const diff = now.getTime() - date.getTime();
		const days = Math.floor(diff / (1000 * 60 * 60 * 24));

		if (days === 0) {
			return date.toLocaleTimeString("en-US", {
				hour: "numeric",
				minute: "2-digit",
				hour12: true,
			});
		} else if (days === 1) {
			return "Yesterday";
		} else if (days < 7) {
			return date.toLocaleDateString("en-US", { weekday: "short" });
		} else {
			return date.toLocaleDateString("en-US", {
				month: "short",
				day: "numeric",
			});
		}
	};

	const getStatusBadge = (status: string) => {
		switch (status) {
			case "completed":
				return (
					<Badge
						variant="default"
						className="bg-green-100 text-green-800 border-green-200"
					>
						<CheckCircle className="w-3 h-3 mr-1" />
						Completed
					</Badge>
				);
			case "accepted":
				return (
					<Badge
						variant="default"
						className="bg-blue-100 text-blue-800 border-blue-200"
					>
						<CheckCircle className="w-3 h-3 mr-1" />
						Accepted
					</Badge>
				);
			case "pending":
				return (
					<Badge
						variant="secondary"
						className="bg-yellow-100 text-yellow-800 border-yellow-200"
					>
						<Clock className="w-3 h-3 mr-1" />
						Pending
					</Badge>
				);
			default:
				return <Badge variant="secondary">{status}</Badge>;
		}
	};

	const getFlagIcon = (reason: string) => {
		switch (reason) {
			case "phone":
				return <Phone className="w-3 h-3" />;
			case "email":
				return <Mail className="w-3 h-3" />;
			default:
				return <Flag className="w-3 h-3" />;
		}
	};

	const getTabCounts = () => ({
		all: conversations.length,
		flagged: conversations.filter((c) => c.is_flagged && !c.admin_dismissed)
			.length,
		flaggedMessages: flaggedMessages.filter((m) => !m.is_dismissed).length,
	});

	const tabCounts = getTabCounts();

	if (loading) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-sage-50 to-sage-100 flex items-center justify-center">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sage-600 mx-auto mb-4"></div>
					<p className="text-gray-600 font-karla">
						Loading platform messages...
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gradient-to-br from-sage-50 to-sage-100">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
				{/* Header */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6 }}
					className="mb-8"
				>
					<div className="flex items-center justify-between">
						<div className="flex items-center space-x-4">
							<Button
								variant="ghost"
								onClick={() => navigate("/admin-dashboard")}
								className="p-2"
							>
								<ArrowLeft className="h-5 w-5" />
							</Button>
							<div>
								<h1 className="text-3xl font-bold text-gray-900 font-karla">
									Message Monitor
								</h1>
								<p className="text-gray-600 font-karla">
									Monitor all platform conversations and flagged messages
								</p>
							</div>
						</div>

						<div className="flex items-center space-x-2">
							{tabCounts.flagged > 0 && (
								<Badge
									variant="destructive"
									className="flex items-center gap-1"
								>
									<AlertTriangle className="w-3 h-3" />
									{tabCounts.flagged} Flagged
								</Badge>
							)}
						</div>
					</div>
				</motion.div>

				{/* Tabs */}
				<Tabs
					defaultValue="conversations"
					value={activeTab}
					onValueChange={setActiveTab}
					className="space-y-6"
				>
					<div className="flex justify-center">
						<TabsList className={`${styles.tabButtons} grid w-fit grid-cols-2 border border-newTheme-lightGrey h-auto`}>
							<TabsTrigger
								value="conversations"
								className={`${styles.tabButton} flex items-center gap-2 font-karla data-[state=active]:bg-newTheme-lime text-black data-[state=inactive]:opacity-50 transition-all duration-300 rounded-md m-0.5`}
							>
								<MessageSquare className="w-4 h-4" />
								All Conversations ({tabCounts.all})
							</TabsTrigger>
							<TabsTrigger
								value="flagged"
								className={`${styles.tabButton} flex items-center gap-2 font-karla data-[state=active]:bg-newTheme-lime text-black data-[state=inactive]:opacity-50 transition-all duration-300 rounded-md m-0.5`}
							>
								<Flag className="w-4 h-4" />
								Flagged Messages ({tabCounts.flaggedMessages})
							</TabsTrigger>
						</TabsList>
					</div>

					{/* Conversations Tab */}
					<TabsContent value="conversations" className="space-y-6">
						{/* Search and Filters */}
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: 0.1 }}
							className="glass-card border-0 shadow-lg p-6 rounded-2xl"
						>
							<div className="flex flex-col lg:flex-row gap-4">
								{/* Search */}
								<div className="relative flex-1">
									<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
									<Input
										type="text"
										placeholder="Search conversations by user names or message content..."
										value={searchTerm}
										onChange={(e) => setSearchTerm(e.target.value)}
										className="pl-10 h-12 text-lg bg-white/80 backdrop-blur-sm border-0 font-karla"
									/>
								</div>

								{/* Filter */}
								<div className="lg:w-64 lg:text-right">
									<select onChange={(e) => setFilterType(e.target.value)} value={filterType} className="h-12 text-lg bg-white/80 backdrop-blur-sm font-karla border border-newTheme-lightGrey rounded-md p-2">
										<option value="all">All Conversations</option>
										<option value="flagged">Flagged Only</option>
										<option value="contact-info">Contains Contact Info</option>
										<option value="recent">Recent (7 days)</option>
										<option value="unread">Has Unread Messages</option>
									</select>
								</div>
							</div>

							{/* Quick Stats */}
							<div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
								<div className="text-center">
									<div className="text-2xl font-bold text-sage-600">
										{filteredConversations.length}
									</div>
									<div className="text-sm text-black font-karla">
										Total Conversations
									</div>
								</div>
								<div className="text-center">
									<div className="text-2xl font-bold text-newTheme-lime">
										{
											conversations.filter(
												(c) => c.is_flagged && !c.admin_dismissed,
											).length
										}
									</div>
									<div className="text-sm text-black font-karla">
										Flagged
									</div>
								</div>
								<div className="text-center">
									<div className="text-2xl font-bold text-newTheme-orange">
										{conversations.filter((c) => c.has_contact_info).length}
									</div>
									<div className="text-sm text-black font-karla">
										Contact Info
									</div>
								</div>
								<div className="text-center">
									<div className="text-2xl font-bold text-newTheme-spruce">
										{conversations.filter((c) => c.unread_count > 0).length}
									</div>
									<div className="text-sm text-black font-karla">
										With Unread
									</div>
								</div>
							</div>
						</motion.div>

						<div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
							{/* Conversations List */}
							<div className="lg:col-span-1">
								<motion.div
									initial={{ opacity: 0, x: -20 }}
									animate={{ opacity: 1, x: 0 }}
									transition={{ duration: 0.6, delay: 0.1 }}
								>
									<Card className="glass-card border-0 shadow-lg bg-gradient-to-br from-primary-50 to-primary-100 border-primary-200">
										<CardHeader>
											<CardTitle className="text-xl font-karla flex items-center">
												<MessageSquare className="h-5 w-5 mr-2 text-primary-600" />
												Platform Conversations
											</CardTitle>
										</CardHeader>
										<CardContent>
											<div className="space-y-3">
												{filteredConversations.length === 0 ? (
													<div className="text-center py-8">
														<MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
														<p className="text-gray-600 font-karla">
															No conversations found
														</p>
														<p className="text-sm text-gray-500 font-karla mt-2">
															{searchTerm
																? "Try adjusting your search terms"
																: "No conversations match the selected filter"}
														</p>
													</div>
												) : (
													filteredConversations.map((conversation, index) => (
														<motion.div
															key={conversation.id}
															initial={{ opacity: 0, y: 10 }}
															animate={{ opacity: 1, y: 0 }}
															transition={{
																duration: 0.6,
																delay: 0.2 + index * 0.05,
															}}
															onClick={() => {
																setSelectedConversation(conversation.id);
																loadConversationMessages(conversation.id);
															}}
															className={`p-4 rounded-lg cursor-pointer transition-all duration-200 ${selectedConversation === conversation.id
																? "bg-primary-50 border-primary-200 border-2"
																: "bg-white/80 hover:bg-white border border-transparent"
																} ${conversation.is_flagged && !conversation.admin_dismissed ? "ring-2 ring-red-200 bg-red-50" : ""}`}
														>
															<div className="space-y-3">
																{/* Users */}
																<div className="flex items-center justify-between">
																	<div className="flex items-center space-x-2">
																		<Baby className="h-4 w-4 text-blue-600" />
																		<span className="font-semibold text-gray-900 font-karla text-sm truncate">
																			{conversation.parent_user.full_name}
																		</span>
																	</div>
																	<div className="flex items-center space-x-2">
																		<Shield className="h-4 w-4 text-purple-600" />
																		<span className="font-semibold text-gray-900 font-karla text-sm truncate">
																			{conversation.doula_user.full_name}
																		</span>
																	</div>
																</div>

																{/* Last Message */}
																{conversation.last_message && (
																	<div>
																		<p className="text-sm text-gray-600 font-karla line-clamp-2">
																			{conversation.last_message.content}
																		</p>
																		<div className="flex items-center justify-between mt-1">
																			<span className="text-xs text-gray-500 font-karla">
																				{conversation.last_message.sender_type}{" "}
																				•{" "}
																				{formatTime(
																					conversation.last_message.created_at,
																				)}
																			</span>
																			{conversation.unread_count > 0 && (
																				<div className="w-5 h-5 bg-primary-600 text-white text-xs rounded-full flex items-center justify-center">
																					{conversation.unread_count}
																				</div>
																			)}
																		</div>
																	</div>
																)}

																{/* Status and Flags */}
																<div className="flex items-center justify-between">
																	{conversation.appointments &&
																		conversation.appointments.length > 0 ? (
																		conversation.appointments.length === 1 ? (
																			getStatusBadge(
																				conversation.appointments[0].status,
																			)
																		) : (
																			<Badge
																				variant="outline"
																				className="text-xs"
																			>
																				{conversation.appointments.length}{" "}
																				Appointments
																			</Badge>
																		)
																	) : conversation.appointment ? (
																		getStatusBadge(
																			conversation.appointment.status,
																		)
																	) : (
																		<Badge
																			variant="outline"
																			className="text-xs"
																		>
																			No Appointment
																		</Badge>
																	)}

																	<div className="flex items-center space-x-1">
																		{conversation.has_contact_info && (
																			<Badge
																				variant="secondary"
																				className="text-xs bg-orange-100 text-orange-800 border-orange-200"
																			>
																				Contact Info
																			</Badge>
																		)}
																		{conversation.is_flagged &&
																			!conversation.admin_dismissed && (
																				<Badge
																					variant="destructive"
																					className="text-xs flex items-center gap-1"
																				>
																					<AlertTriangle className="w-3 h-3" />
																					Flagged
																				</Badge>
																			)}
																	</div>
																</div>

																{/* Dismiss Button for Flagged */}
																{conversation.is_flagged &&
																	!conversation.admin_dismissed && (
																		<Button
																			onClick={(e) => {
																				e.stopPropagation();
																				dismissFlaggedConversation(
																					conversation.id,
																				);
																			}}
																			size="sm"
																			variant="outline"
																			className="w-full text-xs font-karla"
																		>
																			<EyeOff className="w-3 h-3 mr-1" />
																			Dismiss Flag
																		</Button>
																	)}
															</div>
														</motion.div>
													))
												)}
											</div>
										</CardContent>
									</Card>
								</motion.div>
							</div>

							{/* Message Thread */}
							<div className="lg:col-span-2">
								<motion.div
									initial={{ opacity: 0, x: 20 }}
									animate={{ opacity: 1, x: 0 }}
									transition={{ duration: 0.6, delay: 0.2 }}
								>
									{selectedConversation ? (
										<AdminMessageThread
											conversationData={
												filteredConversations.find(
													(conv) => conv.id === selectedConversation,
												)!
											}
											messages={conversationMessages}
											loading={loadingMessages}
										/>
									) : (
										<Card className="glass-card border-0 shadow-lg h-[600px] flex items-center justify-center">
											<div className="text-center">
												<MessageSquare className="h-16 w-16 text-gray-400 mx-auto mb-4" />
												<h3 className="text-lg font-medium text-gray-900 font-karla mb-2">
													Select a conversation
												</h3>
												<p className="text-gray-600 font-karla">
													Choose a conversation to view the message thread
												</p>
											</div>
										</Card>
									)}
								</motion.div>
							</div>
						</div>
					</TabsContent>

					{/* Flagged Messages Tab */}
					<TabsContent value="flagged" className="space-y-6">
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: 0.1 }}
							className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6"
						>
							{flaggedMessages.filter((msg) => !msg.is_dismissed).length ===
								0 ? (
								<div className="col-span-full">
									<Card className="glass-card border-0 shadow-lg">
										<CardContent className="p-12 text-center">
											<CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
											<h3 className="text-xl font-semibold text-gray-900 mb-2 font-karla">
												No Flagged Messages
											</h3>
											<p className="text-gray-600 font-karla">
												All messages have been reviewed. Great job keeping the
												platform safe!
											</p>
										</CardContent>
									</Card>
								</div>
							) : (
								flaggedMessages
									.filter((msg) => !msg.is_dismissed)
									.map((message, index) => {
										const conversation = conversations.find(
											(c) => c.id === message.conversation_id,
										);
										return (
											<motion.div
												key={message.id}
												initial={{ opacity: 0, y: 20 }}
												animate={{ opacity: 1, y: 0 }}
												transition={{ duration: 0.6, delay: 0.1 + index * 0.1 }}
											>
												<Card className="glass-card border-0 shadow-lg hover:shadow-xl transition-all duration-300 border-red-200">
													<CardHeader className="pb-4">
														<div className="flex items-start justify-between">
															<div className="flex items-center space-x-2">
																<Badge
																	variant="destructive"
																	className="flex items-center gap-1"
																>
																	{getFlagIcon(message.flag_reason)}
																	{message.flag_reason}
																</Badge>
																<span className="text-xs text-gray-500 font-karla">
																	{formatTime(message.created_at)}
																</span>
															</div>
															<Button
																onClick={() =>
																	dismissFlaggedMessage(message.id)
																}
																size="sm"
																variant="outline"
																className="text-xs font-karla"
															>
																<EyeOff className="w-3 h-3 mr-1" />
																Dismiss
															</Button>
														</div>
													</CardHeader>

													<CardContent className="space-y-4">
														{/* Message Content */}
														<div className="bg-red-50 p-3 rounded-lg border border-red-200">
															<p className="text-sm text-gray-700 font-karla">
																"{message.content}"
															</p>
														</div>

														{/* Conversation Info */}
														{conversation && (
															<div className="space-y-2">
																<div className="flex items-center justify-between text-sm">
																	<span className="text-gray-600 font-karla">
																		From:
																	</span>
																	<span className="font-medium text-gray-900 font-karla">
																		{message.sender_type === "parent"
																			? conversation.parent_user.full_name
																			: conversation.doula_user.full_name}{" "}
																		({message.sender_type})
																	</span>
																</div>
																<div className="flex items-center justify-between text-sm">
																	<span className="text-gray-600 font-karla">
																		Conversation:
																	</span>
																	<span className="font-medium text-gray-900 font-karla">
																		{conversation.parent_user.full_name} ↔{" "}
																		{conversation.doula_user.full_name}
																	</span>
																</div>
																<div className="flex items-center justify-between text-sm">
																	<span className="text-gray-600 font-karla">
																		Appointment:
																	</span>
																	{conversation.appointments &&
																		conversation.appointments.length > 0 ? (
																		conversation.appointments.length === 1 ? (
																			getStatusBadge(
																				conversation.appointments[0].status,
																			)
																		) : (
																			<Badge
																				variant="outline"
																				className="text-xs"
																			>
																				{conversation.appointments.length} Total
																			</Badge>
																		)
																	) : conversation.appointment ? (
																		getStatusBadge(
																			conversation.appointment.status,
																		)
																	) : (
																		<Badge
																			variant="outline"
																			className="text-xs"
																		>
																			None
																		</Badge>
																	)}
																</div>
															</div>
														)}

														{/* Actions */}
														<div className="flex space-x-2 pt-2 border-t border-gray-100">
															<Button
																onClick={() => {
																	setSelectedConversation(
																		message.conversation_id,
																	);
																	loadConversationMessages(
																		message.conversation_id,
																	);
																	setActiveTab("conversations");
																}}
																size="sm"
																className="flex-1 bg-primary-500 hover:bg-primary-600 text-white font-karla"
															>
																<Eye className="w-3 h-3 mr-1" />
																View Thread
															</Button>
														</div>
													</CardContent>
												</Card>
											</motion.div>
										);
									})
							)}
						</motion.div>
					</TabsContent>
				</Tabs>
			</div>
		</div>
	);
}
